"use client";

import React, { useCallback, useRef, useState } from "react";

const ALLOWED_EXTENSIONS = [".stl", ".stp", ".obj", ".3mf"];
const MAX_FILE_SIZE_MB = 50;

interface ContactFileUploaderProps {
  file: File | null;
  onFileChange: (file: File | null) => void;
  error?: string;
  onError: (msg: string) => void;
}

export default function ContactFileUploader({
  file,
  onFileChange,
  error,
  onError,
}: ContactFileUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const isValidFile = useCallback((f: File): boolean => {
    const ext = "." + f.name.split(".").pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      onError(`Formato non supportato. Accettati: ${ALLOWED_EXTENSIONS.join(", ")}`);
      return false;
    }
    if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      onError(`File troppo grande. Massimo ${MAX_FILE_SIZE_MB}MB`);
      return false;
    }
    const parts = f.name.split(".");
    if (parts.length > 2) {
      onError("Non sono ammesse doppie estensioni nel nome del file");
      return false;
    }
    return true;
  }, [onError]);

  const handleFile = useCallback(
    (f: File) => {
      if (isValidFile(f)) {
        onError("");
        onFileChange(f);
      }
    },
    [isValidFile, onError, onFileChange]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) handleFile(dropped);
    },
    [handleFile]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0];
      if (selected) handleFile(selected);
      e.target.value = "";
    },
    [handleFile]
  );

  const handleRemove = useCallback(() => {
    onFileChange(null);
    onError("");
  }, [onFileChange, onError]);

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-secondary-500 mb-2">
        Allega un file 3D <span className="text-secondary-400">(opzionale)</span>
      </label>

      {!file ? (
        <div
          className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 cursor-pointer
            ${dragOver ? "border-primary-500 bg-primary-50/50" : "border-gray-300 hover:border-primary-400 hover:bg-primary-50/30"}`}
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onClick={() => inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ALLOWED_EXTENSIONS.join(",")}
            onChange={handleChange}
            className="hidden"
          />
          <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p className="mt-3 text-sm font-medium text-secondary-500">
            Trascina il tuo file 3D qui
          </p>
          <p className="mt-1 text-xs text-secondary-400">
            oppure clicca per selezionare
          </p>
          <p className="mt-2 text-xs text-secondary-400">
            Formati accettati: STL, STP, OBJ, 3MF — max {MAX_FILE_SIZE_MB}MB
          </p>
        </div>
      ) : (
        <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-secondary-500 truncate">
              {file.name}
            </p>
            <p className="text-xs text-secondary-400">
              {(file.size / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="flex-shrink-0 text-secondary-400 hover:text-red-500 transition-colors"
            aria-label="Rimuovi file"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600 font-medium">{error}</p>
      )}
    </div>
  );
}